import { useState, useEffect } from 'react';
import { COOWN_SUPPORT_EMAIL } from '../config/constants';
import {
  classifySubmission,
  getSubmissionListingMode,
  formatSubmissionListingModeLabel,
  formatSubmissionStatusLabel,
  submissionStatusBadgeClass,
  submissionSortTime,
  getSubmissionPhotoUrls,
  getSubmissionLiveSiteUrl,
  getSubmissionDeletionActor,
  formatListerProposedShareSummary,
  formatShareStructureSummary,
  saleListingNeedsRaisingApproval,
  listerCanRemoveSubmission,
} from '../utils/submissions';
import apiClient from '../utils/api';
import SubmissionPhotoGallery from '../components/submission/SubmissionPhotoGallery';
import PipelineProgress from '../components/submission/PipelineProgress';
import PropertyLocationMap from '../components/shared/PropertyLocationMap';
import ListerPricingBanner from '../components/agent/ListerPricingBanner';
import ListingMessagesPanel from '../components/agent/ListingMessagesPanel';
import PropertyDocumentsPanel from '../components/property/PropertyDocumentsPanel';
import PageRefinedHero from '../components/layout/PageRefinedHero';

const AgentDashboardPage = ({
  submissions,
  properties = [],
  isLoggedIn,
  user,
  currentUid,
  onRefresh,
  addToast,
  onUnreadChange,
  onNotifyNewMessages,
  onActiveConversationChange,
  setPage,
  onOpenListing
}) => {
  const [view, setView] = useState("submissions");
  const [listingFilter, setListingFilter] = useState("all");
  const [listingModeFilter, setListingModeFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [deletedSubmissions, setDeletedSubmissions] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);

  const listingStats = {
    total: submissions.length,
    inReview: submissions.filter((s) => classifySubmission(s.status) === "pending").length,
    approved: submissions.filter((s) => classifySubmission(s.status) === "approved").length,
    rejected: submissions.filter((s) => classifySubmission(s.status) === "rejected").length,
    rent: submissions.filter((s) => getSubmissionListingMode(s) === "rent").length,
    sale: submissions.filter((s) => getSubmissionListingMode(s) === "sale").length
  };

  const filteredListings = [...submissions]
    .filter((s) => {
      if (listingFilter !== "all" && classifySubmission(s.status) !== listingFilter) return false;
      if (listingModeFilter !== "all" && getSubmissionListingMode(s) !== listingModeFilter) return false;
      return true;
    })
    .sort((a, b) => submissionSortTime(b) - submissionSortTime(a));

  const loadListings = async () => {
    if (!onRefresh) return;
    setLoading(true);
    try {
      await onRefresh();
    } catch (err) {
      addToast?.(err.message || "Could not load your listings.", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadDeletedListings = async () => {
    setLoadingDeleted(true);
    try {
      const res = await apiClient.getMyDeletedSubmissions();
      if (res.data && Array.isArray(res.data)) setDeletedSubmissions(res.data);
    } catch (err) {
      addToast?.(err.message || "Could not load deleted listings.", "error");
    } finally {
      setLoadingDeleted(false);
    }
  };

  const handleDeleteListing = async (sub) => {
    if (!window.confirm(`Remove "${sub.title}" from your account? CoOwn admins keep a copy for fraud review.`)) return;
    try {
      await apiClient.deleteSubmission(sub.id);
      addToast("Listing removed. You can find it under the Deleted tab.", "success");
      await loadListings();
      await loadDeletedListings();
    } catch (err) {
      addToast(err.message || "Could not remove listing.", "error");
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      loadListings();
      loadDeletedListings();
    }
  }, [isLoggedIn]);

  if (!isLoggedIn) {
    return (
      <div className="coown-page coown-page--hero">
        <PageRefinedHero
          badge="Lister dashboard"
          title="My"
          titleAccent="listings"
          subtitle="Sign in to track every property you submitted — from first review through live on site."
          benefits={[
            { icon: 'fas fa-clipboard-list', title: 'Submission pipeline', subtitle: 'See status at every stage' },
            { icon: 'fas fa-comments', title: 'Buyer messages', subtitle: 'Reply to inquiries in one place' },
            { icon: 'fas fa-chart-bar', title: 'Performance', subtitle: 'Monitor views and engagement' },
          ]}
        />
        <div className="coown-page-body coown-page-body--narrow">
          <div className="coown-page-empty">
            <i className="fas fa-list-alt coown-page-empty__icon" aria-hidden="true"></i>
            <h3>Sign in to continue</h3>
            <p>Access your submissions, messages, and listing analytics after you log in.</p>
            <button type="button" className="btn btn-primary" onClick={() => setPage?.("login")}>Sign in</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coown-page coown-page--hero">
      <PageRefinedHero
        badge="Lister dashboard"
        title="My"
        titleAccent="listings"
        subtitle="All properties you submitted — status updates when an admin advances your application."
        benefits={[
          { icon: 'fas fa-hourglass-half', title: `${listingStats.inReview} in review`, subtitle: 'Awaiting admin action' },
          { icon: 'fas fa-check-circle', title: `${listingStats.approved} approved`, subtitle: 'Live or cleared to publish' },
          { icon: 'fas fa-home', title: `${listingStats.total} total`, subtitle: 'Sale and rent submissions' },
        ]}
      />

      <div className="coown-page-body">
        <div className="coown-page-toolbar">
          <div className="coown-page-toolbar__lead">
            <h2>Manage submissions</h2>
            <p>Refresh for the latest pipeline status and agent messages.</p>
          </div>
          <div className="coown-page-toolbar__actions">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={async () => {
                await loadListings();
                await loadDeletedListings();
              }}
              disabled={loading || loadingDeleted}
            >
              <i className={`fas fa-sync${loading || loadingDeleted ? " fa-spin" : ""}`}></i> Refresh
            </button>
          </div>
        </div>
      <div className="tab-nav">
        <button className={`tab-btn ${view==="submissions"?"active":""}`} onClick={()=>setView("submissions")}>My Submissions</button>
        <button className={`tab-btn ${view==="messages"?"active":""}`} onClick={()=>setView("messages")}>Messages</button>
        <button className={`tab-btn ${view==="deleted"?"active":""}`} onClick={()=>setView("deleted")}>
          Deleted{deletedSubmissions.length > 0 ? ` (${deletedSubmissions.length})` : ""}
        </button>
        <button className={`tab-btn ${view==="analytics"?"active":""}`} onClick={()=>setView("analytics")}>Performance</button>
      </div>
      {view === "messages" && (
        <div style={{ marginBottom: "1.5rem" }}>
          <p style={{ color: "var(--text-light)", fontSize: "0.9rem", marginBottom: "1rem" }}>
            Reply to buyers who contacted you about your listings. You can also open the full inbox from the menu under Messages.
          </p>
          <ListingMessagesPanel
            user={user}
            currentUid={currentUid}
            addToast={addToast}
            onUnreadChange={onUnreadChange}
            onNotifyNewMessages={onNotifyNewMessages}
            onActiveConversationChange={onActiveConversationChange}
            onOpenListing={onOpenListing}
          />
        </div>
      )}

      {view === "submissions" && (
        <>
      <div className="submission-queue-filters" style={{ marginBottom: "1.25rem" }}>
        {[
          { key: "all", label: `All (${listingStats.total})` },
          { key: "pending", label: `In review (${listingStats.inReview})` },
          { key: "approved", label: `Approved (${listingStats.approved})` },
          { key: "rejected", label: `Rejected (${listingStats.rejected})` }
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            className={`btn btn-sm ${listingFilter === f.key ? "btn-primary" : "btn-outline"}`}
            onClick={() => setListingFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="submission-mode-filters" style={{ marginBottom: "1.25rem" }}>
        {[
          { key: "all", label: `All types (${listingStats.total})` },
          { key: "sale", label: `For sale / buy (${listingStats.sale})` },
          { key: "rent", label: `For rent (${listingStats.rent})` }
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            className={`submission-mode-filter ${listingModeFilter === f.key ? "active" : ""}`}
            onClick={() => setListingModeFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
        </>
      )}
      {view === "deleted" && (
        <div>
          <p style={{ color: "var(--text-light)", fontSize: "0.9rem", marginBottom: "1rem" }}>
            Listings removed by you or by CoOwn. Removed listings are not shown on the public site. Contact{" "}
            <a href={`mailto:${COOWN_SUPPORT_EMAIL}`}>{COOWN_SUPPORT_EMAIL}</a> if you believe a listing was removed in error.
          </p>
          <div style={{ marginBottom: "1rem" }}>
            <button type="button" className="btn btn-outline btn-sm" onClick={loadDeletedListings} disabled={loadingDeleted}>
              <i className={`fas fa-sync${loadingDeleted ? " fa-spin" : ""}`}></i> Refresh deleted
            </button>
          </div>
          {loadingDeleted && deletedSubmissions.length === 0 && (
            <p style={{ color: "var(--text-light)", textAlign: "center", padding: "2rem" }}>
              <i className="fas fa-spinner fa-spin"></i> Loading deleted listings…
            </p>
          )}
          {!loadingDeleted && deletedSubmissions.length === 0 && (
            <p style={{ color: "var(--text-light)", textAlign: "center", padding: "2rem" }}>No deleted listings.</p>
          )}
          {deletedSubmissions.map((sub) => {
            const deletion = getSubmissionDeletionActor(sub);
            const removedByAdmin = deletion.role === "admin";
            return (
              <div key={sub.id} className="agent-card" style={{ borderLeft: "4px solid var(--danger)", opacity: 0.92 }}>
                <div className="agent-header">
                  <div className="agent-company">
                    <div className="agent-logo">{(sub.agentName || "?").split(" ").map((n) => n[0]).join("").slice(0, 2)}</div>
                    <div>
                      <h4 style={{ margin: "0 0 0.25rem" }}>{sub.title}</h4>
                      <p style={{ color: "var(--text-light)", fontSize: "0.85rem", margin: 0 }}>
                        {sub.location} · Deleted {sub.deletedDate || "—"}
                      </p>
                      <div className="deletion-actor-badges" style={{ marginTop: "0.35rem" }}>
                        {removedByAdmin ? (
                          <span className="status-badge deletion-actor-badge--admin">Removed by CoOwn</span>
                        ) : (
                          <span className="status-badge deletion-actor-badge--lister">Removed by you</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <span className="status-badge status-rejected">Deleted</span>
                </div>
                <div
                  style={{
                    marginTop: "0.75rem",
                    padding: "0.75rem 0.85rem",
                    background: removedByAdmin ? "rgba(220, 53, 69, 0.06)" : "var(--bg)",
                    borderRadius: "8px",
                    fontSize: "0.88rem",
                    lineHeight: 1.5,
                    border: removedByAdmin ? "1px solid rgba(220, 53, 69, 0.15)" : "1px solid var(--border)"
                  }}
                >
                  {removedByAdmin ? (
                    <>
                      <i className="fas fa-info-circle" style={{ color: "var(--danger)", marginRight: "0.4rem" }}></i>
                      CoOwn removed this listing from the public site. It is no longer visible to investors.
                      {deletion.email ? ` (${deletion.email})` : ""}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-user-check" style={{ color: "var(--primary)", marginRight: "0.4rem" }}></i>
                      You removed this listing from your dashboard.
                    </>
                  )}
                </div>
                <p style={{ color: "var(--text-light)", fontSize: "0.8rem", margin: "0.65rem 0 0" }}>
                  Last pipeline status: {formatSubmissionStatusLabel(sub.status)}
                </p>
              </div>
            );
          })}
        </div>
      )}
      {view === "submissions" && (
        <div>
          {loading && submissions.length === 0 && (
            <p style={{color:"var(--text-light)",textAlign:"center",padding:"2rem"}}><i className="fas fa-spinner fa-spin"></i> Loading your submissions…</p>
          )}
          {!loading && submissions.length === 0 && (
            <p style={{color:"var(--text-light)",textAlign:"center",padding:"2rem"}}>No submissions yet. Use List Property to submit a listing.</p>
          )}
          {!loading && submissions.length > 0 && filteredListings.length === 0 && (
            <p style={{color:"var(--text-light)",textAlign:"center",padding:"2rem"}}>No listings in this category.</p>
          )}
          {filteredListings.map(sub => (
            <div key={sub.id} className="agent-card" style={classifySubmission(sub.status) === "rejected" ? { borderLeft: "4px solid var(--danger)", opacity: 0.95 } : undefined}>
              <div className="agent-header">
                <div className="agent-company">
                  <div className="agent-logo">{(sub.agentName || "?").split(" ").map(n=>n[0]).join("").slice(0,2)}</div>
                  <div>
                    <div className="submission-row-title-row" style={{ marginBottom: "0.2rem" }}>
                      <h4 className="submission-row-title-text" style={{ margin: 0 }}>{sub.title}</h4>
                      <span className={`status-badge listing-mode-badge status-${getSubmissionListingMode(sub) === "rent" ? "pending" : "approved"}`}>
                        {formatSubmissionListingModeLabel(sub, { short: true })}
                      </span>
                    </div>
                    <p style={{color:"var(--text-light)",fontSize:"0.85rem"}}>{sub.location} · Submitted {sub.submittedDate}</p>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.35rem" }}>
                  <span className={`status-badge status-${submissionStatusBadgeClass(sub.status)}`}>{formatSubmissionStatusLabel(sub.status)}</span>
                  <span className={`status-badge listing-mode-badge status-${getSubmissionListingMode(sub) === "rent" ? "pending" : "approved"}`} style={{ fontSize: "0.72rem" }}>
                    {formatSubmissionListingModeLabel(sub)}
                  </span>
                </div>
              </div>
              {getSubmissionPhotoUrls(sub).length === 0 ? (
                <div className="agent-listing-no-photos">
                  <i className="fas fa-image" aria-hidden="true"></i>
                  No photos on this listing yet. If you just uploaded images, click <strong>Refresh</strong>.
                </div>
              ) : (
                <SubmissionPhotoGallery photos={getSubmissionPhotoUrls(sub)} title={sub.title || "Listing"} />
              )}
              <div style={{marginBottom:"1.5rem"}}>
                <PipelineProgress currentStatus={sub.status} liveListingUrl={getSubmissionLiveSiteUrl(sub)} />
              </div>
              {getSubmissionListingMode(sub) === "sale" ? <ListerPricingBanner sub={sub} /> : null}
              <div style={{display:"flex",gap:"2rem",fontSize:"0.9rem",color:"var(--text-light)",flexWrap:"wrap"}}>
                <span>
                  <i className={`fas fa-${getSubmissionListingMode(sub) === "rent" ? "key" : "hand-holding-usd"}`} style={{color:"var(--primary)"}}></i>
                  {" "}{formatSubmissionListingModeLabel(sub)}
                </span>
                <span><i className="fas fa-tag" style={{color:"var(--primary)"}}></i> {sub.type}</span>
                {getSubmissionListingMode(sub) === "rent" && Number(sub.monthlyRent) > 0 ? (
                  <span>
                    <i className="fas fa-dollar-sign" style={{color:"var(--primary)"}}></i>
                    {` $${Number(sub.monthlyRent).toLocaleString()}/mo rent`}
                  </span>
                ) : null}
                <span><i className="fas fa-calendar" style={{color:"var(--primary)"}}></i> Last update: {sub.pipelineDate}</span>
                {formatListerProposedShareSummary(sub) && (
                  <span><i className="fas fa-chart-pie" style={{color:"var(--primary)"}}></i> Your suggestion: {formatListerProposedShareSummary(sub)}</span>
                )}
                {sub?.raisingStructureApproved === true && formatShareStructureSummary(sub) && (
                  <span><i className="fas fa-check-circle" style={{color:"var(--success)"}}></i> {formatShareStructureSummary(sub)}</span>
                )}
                {saleListingNeedsRaisingApproval(sub) && (
                  <span><i className="fas fa-hourglass-half" style={{color:"var(--warning)"}}></i> Share structure pending admin approval</span>
                )}
              </div>
              {sub.location && (
                <div style={{ marginTop: "1rem" }}>
                  <PropertyLocationMap location={sub.location} height={200} title="Listing location" />
                </div>
              )}
              {sub.notes && (
                <div style={{marginTop:"1rem",padding:"0.8rem",background:"var(--bg)",borderRadius:"8px",fontSize:"0.85rem",color:"var(--text-light)"}}>
                  <i className="fas fa-info-circle" style={{color:"var(--info)",marginRight:"0.5rem"}}></i> {sub.notes}
                </div>
              )}
              {sub.publishedPropertyId ? (
                <div style={{ marginTop: "1.25rem" }}>
                  <PropertyDocumentsPanel
                    propertyId={sub.publishedPropertyId}
                    isLoggedIn={isLoggedIn}
                    addToast={addToast}
                  />
                </div>
              ) : null}
              <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                {(() => {
                  const removal = listerCanRemoveSubmission(sub, properties);
                  if (!removal.allowed) {
                    return (
                      <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-light)" }}>
                        <i className="fas fa-lock" aria-hidden="true"></i> {removal.reason}
                      </p>
                    );
                  }
                  return (
                    <button type="button" className="btn btn-outline btn-sm" style={{ color: "var(--danger)", borderColor: "var(--danger)" }} onClick={() => handleDeleteListing(sub)}>
                      <i className="fas fa-trash-alt"></i> Remove listing
                    </button>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      )}
      {view === "analytics" && (
        <div className="detail-card">
          <h3>Performance Overview</h3>
          <p style={{ color: "var(--text-light)", fontSize: "0.9rem", marginTop: "0.5rem" }}>
            Summary of all properties you have submitted to CoOwn.
          </p>
          <div className="stats-row" style={{gridTemplateColumns:"repeat(4,1fr)",marginTop:"1rem"}}>
            <div className="stat-card"><label>Total Submitted</label><value>{listingStats.total}</value></div>
            <div className="stat-card" style={{borderLeftColor:"var(--warning)"}}><label>In Review</label><value>{listingStats.inReview}</value></div>
            <div className="stat-card" style={{borderLeftColor:"var(--success)"}}><label>Approved / Live</label><value>{listingStats.approved}</value></div>
            <div className="stat-card" style={{borderLeftColor:"var(--danger)"}}><label>Rejected</label><value>{listingStats.rejected}</value></div>
          </div>
          {listingStats.rejected > 0 && (
            <div style={{ marginTop: "1.5rem", padding: "1rem", background: "#fff5f5", borderRadius: 12, border: "1px solid #f5c6cb" }}>
              <h4 style={{ marginBottom: "0.5rem", color: "var(--danger)" }}><i className="fas fa-times-circle"></i> Rejected listings</h4>
              <ul style={{ margin: 0, paddingLeft: "1.25rem", color: "var(--text-light)", fontSize: "0.9rem" }}>
                {submissions.filter((s) => classifySubmission(s.status) === "rejected").map((s) => (
                  <li key={s.id} style={{ marginBottom: "0.35rem" }}>
                    <strong>{s.title}</strong> — {s.notes || "No reason provided"}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default AgentDashboardPage;
